import React from 'react';
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CartProvider } from './contexts/CartContext';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/toaster';
import { setupAxios } from './lib/axiosSetup';

setupAxios();

// Layout
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// Pages
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Category from './pages/Category';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import About from './pages/About';
import Contact from './pages/Contact';
import Login from './pages/Login';
import Register from './pages/Register';
import Account from './pages/Account';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TrackOrder from './pages/TrackOrder';
import PaymentSuccess from './pages/PaymentSuccess';
import BuyerDashboard from './pages/BuyerDashboard';

// Admin Pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminOrders from './pages/admin/AdminOrders';
import AdminUsers from './pages/admin/AdminUsers';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminWebmail from './pages/admin/AdminWebmail';
import AdminSettings from './pages/admin/AdminSettings';
import AdminCategories from './pages/admin/AdminCategories';
import AdminPricing from './pages/admin/AdminPricing';
import AdminProductVisibility from './pages/admin/AdminProductVisibility';

// USKE Procurement Portal pages
import { GovCartProvider } from './contexts/GovCartContext';
import GovLayout from './pages/gov/GovLayout';
import GovLanding from './pages/gov/GovLanding';
import GovLogin from './pages/gov/GovLogin';
import GovDashboard from './pages/gov/GovDashboard';
import GovCatalog from './pages/gov/GovCatalog';
import GovCart from './pages/gov/GovCart';
import GovOrders from './pages/gov/GovOrders';
import GovOrderDetail from './pages/gov/GovOrderDetail';
import GovApprovals from './pages/gov/GovApprovals';
import GovAgencyAdmin from './pages/gov/GovAgencyAdmin';
import GovGovernance from './pages/gov/GovGovernance';
import GovReports from './pages/gov/GovReports';
import GovMFA from './pages/gov/GovMFA';
import GovPunchoutCatalog from './pages/gov/GovPunchoutCatalog';
import GovPunchoutAdmin from './pages/gov/GovPunchoutAdmin';
import GovHelp from './pages/gov/GovHelp';
import GovAccessRequests from './pages/gov/GovAccessRequests';
import GovProfile from './pages/gov/GovProfile';
import GovChangePassword from './pages/gov/GovChangePassword';
import GovForgotPassword from './pages/gov/GovForgotPassword';
import GovResetPassword from './pages/gov/GovResetPassword';
import GovAgencyUsers from './pages/gov/GovAgencyUsers';
import GovCatalogImport from './pages/gov/GovCatalogImport';
import GovInvoices from './pages/gov/GovInvoices';
import GovStatements from './pages/gov/GovStatements';
import GovPaymentInstructions from './pages/gov/GovPaymentInstructions';
import AdminBankingSettings from './pages/gov/AdminBankingSettings';
import GovAdminProducts from './pages/gov/GovAdminProducts';
import PublicPay from './pages/PublicPay';
import GovFulfilment from './pages/gov/GovFulfilment';
import GovFinancialReports from './pages/gov/GovFinancialReports';
import GovFinanceDashboard from './pages/gov/GovFinanceDashboard';
import AcceptInvitation from './pages/gov/AcceptInvitation';
import Delivery from './pages/Delivery';
import { isPortalSubdomain } from './lib/portal';

// Preserves the suffix when redirecting /gov/* → /portal/*
const GovToPortalRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const next = pathname.replace(/^\/gov/, '/portal') + search + hash;
  return <Navigate to={next} replace />;
};

function App() {
  const portalMode = isPortalSubdomain();
  // Standalone portal pages, registered without the /gov prefix.
  // Rendered FIRST so they shadow retail routes when on the portal hostname.
  const PortalRoutes = (
    <>
      <Route path="/" element={<GovLanding />} />
      <Route path="/login" element={<GovLogin />} />
      <Route path="/pay/:token" element={<PublicPay />} />
      <Route path="/forgot-password" element={<GovForgotPassword />} />
      <Route path="/reset-password" element={<GovResetPassword />} />
      <Route path="/punchout/catalog" element={<GovPunchoutCatalog />} />
      <Route path="/delivery" element={<Delivery />} />
      <Route path="/" element={<GovLayout />}>
        <Route path="dashboard" element={<GovDashboard />} />
        <Route path="profile" element={<GovProfile />} />
        <Route path="change-password" element={<GovChangePassword />} />
        <Route path="users" element={<GovAgencyUsers />} />
        <Route path="catalog" element={<GovCatalog />} />
        <Route path="cart" element={<GovCart />} />
        <Route path="orders" element={<GovOrders />} />
        <Route path="orders/:id" element={<GovOrderDetail />} />
        <Route path="approvals" element={<GovApprovals />} />
        <Route path="invoices" element={<GovInvoices />} />
        <Route path="statements" element={<GovStatements />} />
        <Route path="payment-instructions" element={<GovPaymentInstructions />} />
        <Route path="admin/banking" element={<AdminBankingSettings />} />
        <Route path="admin/products" element={<GovAdminProducts />} />
        <Route path="fulfilment" element={<GovFulfilment />} />
        <Route path="financial-reports" element={<GovFinancialReports />} />
        <Route path="admin/finance" element={<GovFinanceDashboard />} />
        <Route path="security" element={<GovMFA />} />
        <Route path="admin/agencies" element={<GovAgencyAdmin />} />
        <Route path="admin/reports" element={<GovReports />} />
        <Route path="admin/fulfillment" element={<GovOrders />} />
        <Route path="admin/punchout" element={<GovPunchoutAdmin />} />
        <Route path="admin/catalog-import" element={<GovCatalogImport />} />
        <Route path="admin/access-requests" element={<GovAccessRequests />} />
        <Route path="help" element={<GovHelp />} />
        <Route path="help/:slug" element={<GovHelp />} />
        <Route path="agency" element={<GovAgencyAdmin />} />
      </Route>
    </>
  );

  return (
    <AuthProvider>
      <CartProvider>
        <GovCartProvider>
          <BrowserRouter>
          <div className="App">
            <Routes>
              {portalMode && PortalRoutes}
              {!portalMode && (
                <>
                  {/* Public Routes with Navbar/Footer */}
                  <Route path="/" element={<><Navbar /><Home /><Footer /></>} />
                  <Route path="/products" element={<><Navbar /><Products /><Footer /></>} />
                  <Route path="/product/:id" element={<><Navbar /><ProductDetail /><Footer /></>} />
                  <Route path="/category/:categoryId" element={<><Navbar /><Category /><Footer /></>} />
                  <Route path="/cart" element={<><Navbar /><Cart /><Footer /></>} />
                  <Route path="/checkout" element={<><Navbar /><Checkout /><Footer /></>} />
                  <Route path="/order-confirmation" element={<><Navbar /><OrderConfirmation /><Footer /></>} />
                  <Route path="/about" element={<><Navbar /><About /><Footer /></>} />
                  <Route path="/contact" element={<><Navbar /><Contact /><Footer /></>} />
                  <Route path="/login" element={<><Navbar /><Login /><Footer /></>} />
                  <Route path="/register" element={<><Navbar /><Register /><Footer /></>} />
                  <Route path="/account" element={<><Navbar /><Account /><Footer /></>} />
                  <Route path="/forgot-password" element={<><Navbar /><ForgotPassword /><Footer /></>} />
                  <Route path="/reset-password" element={<><Navbar /><ResetPassword /><Footer /></>} />
                  <Route path="/track-order" element={<><Navbar /><TrackOrder /><Footer /></>} />
                  <Route path="/payment-success" element={<><Navbar /><PaymentSuccess /><Footer /></>} />
                  <Route path="/dashboard" element={<><Navbar /><BuyerDashboard /><Footer /></>} />

                  {/* Admin Routes */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="products" element={<AdminProducts />} />
                    <Route path="categories" element={<AdminCategories />} />
                    <Route path="orders" element={<AdminOrders />} />
                    <Route path="pricing" element={<AdminPricing />} />
                    <Route path="visibility" element={<AdminProductVisibility />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="customers" element={<AdminCustomers />} />
                    <Route path="webmail" element={<AdminWebmail />} />
                    <Route path="settings" element={<AdminSettings />} />
                  </Route>

                  {/* USKE Procurement Portal (path-based; lives at /portal/* on the main domain) */}
                  <Route path="/portal" element={<GovLanding />} />
                  <Route path="/portal/login" element={<GovLogin />} />
                  <Route path="/portal/forgot-password" element={<GovForgotPassword />} />
                  <Route path="/portal/reset-password" element={<GovResetPassword />} />
                  <Route path="/portal/accept-invitation" element={<AcceptInvitation />} />
                  <Route path="/portal/punchout/catalog" element={<GovPunchoutCatalog />} />
                  <Route path="/pay/:token" element={<PublicPay />} />
                  <Route path="/delivery" element={<Delivery />} />
                  <Route path="/portal" element={<GovLayout />}>
                    <Route path="dashboard" element={<GovDashboard />} />
                    <Route path="profile" element={<GovProfile />} />
                    <Route path="change-password" element={<GovChangePassword />} />
                    <Route path="users" element={<GovAgencyUsers />} />
                    <Route path="catalog" element={<GovCatalog />} />
                    <Route path="cart" element={<GovCart />} />
                    <Route path="orders" element={<GovOrders />} />
                    <Route path="orders/:id" element={<GovOrderDetail />} />
                    <Route path="invoices" element={<GovInvoices />} />
                    <Route path="statements" element={<GovStatements />} />
                    <Route path="payment-instructions" element={<GovPaymentInstructions />} />
        <Route path="admin/banking" element={<AdminBankingSettings />} />
        <Route path="admin/products" element={<GovAdminProducts />} />
                    <Route path="fulfilment" element={<GovFulfilment />} />
                    <Route path="financial-reports" element={<GovFinancialReports />} />
                    <Route path="admin/finance" element={<GovFinanceDashboard />} />
                    <Route path="approvals" element={<GovApprovals />} />
                    <Route path="security" element={<GovMFA />} />
                    <Route path="admin/agencies" element={<GovAgencyAdmin />} />
                    <Route path="admin/reports" element={<GovReports />} />
                    <Route path="admin/fulfillment" element={<GovOrders />} />
                    <Route path="admin/punchout" element={<GovPunchoutAdmin />} />
                    <Route path="admin/catalog-import" element={<GovCatalogImport />} />
                    <Route path="admin/access-requests" element={<GovAccessRequests />} />
                    <Route path="help" element={<GovHelp />} />
                    <Route path="help/:slug" element={<GovHelp />} />
                    <Route path="agency" element={<GovAgencyAdmin />} />
                    <Route path="divisions" element={<Navigate to="/portal/governance" replace />} />
                    <Route path="governance" element={<GovGovernance />} />
                  </Route>

                  {/* Backward-compat: old /gov/* URLs redirect to /portal/* */}
                  <Route path="/gov" element={<GovToPortalRedirect />} />
                  <Route path="/gov/*" element={<GovToPortalRedirect />} />
                </>
              )}
            </Routes>
            <Toaster />
          </div>
        </BrowserRouter>
        </GovCartProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
